import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { collapseAnimation, rotateAnimation } from "angular-animations";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { FormArray, FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { HelpersService } from "../../../shared/services/helpers.service";
import { NDCApiService } from "../../../shared/services/ndc-api.service";
import { Process } from "../../../shared/interfaces/show-process";
import { TranslateService } from "@ngx-translate/core";
import { NotificationService } from "../../../shared/services/notification.service";

interface OsiInfo {
  actionCode: string;
  osiCode: string;
  passengerRef: string;
  text: string;
}

@Component({
  selector: "app-osi-details",
  templateUrl: "./osi-details.component.html",
  styleUrls: ["./osi-details.component.scss"],
  animations: [rotateAnimation({ duration: 500 }), collapseAnimation()],
})
export class OsiDetailsComponent implements OnInit {
  @Input() otherServiceInformation: OsiInfo[];
  @Input() orderID: string;
  @Input() passengers: any[];
  @Input() isPDFExtract: boolean;
  @Output() emitOrderUpdateSuccess = new EventEmitter();

  osiForm: FormGroup;
  applyOsiByPassengerTypeDisabled = {};
  selectedPassengersPerOsi = {};

  passengersExpanded = {};

  isOsiDetailsOpened = true;

  orderUpdateProcess: Process = {
    isProcess: false,
    processTitle: this.translate.instant("ORDER_DETAIL.PROCESSES.ORDER_UPDATE"),
  };

  constructor(
    private modalService: NgbModal,
    private fb: FormBuilder,
    private helpers: HelpersService,
    private ndcApiService: NDCApiService,
    private translate: TranslateService,
    private _notificationSvc: NotificationService,
  ) { }

  ngOnInit(): void {
    this.otherServiceInformation = this.groupOsiEntries(
      this.otherServiceInformation,
    );
  }

  groupOsiEntries(osiArr: OsiInfo[]): OsiInfo[] {
    const result: OsiInfo[] = [];
    const map: Map<string, OsiInfo> = new Map();

    osiArr?.forEach((item) => {
      const key = `${item.actionCode}-${item.osiCode}-${item.text}`;
      if (!map.has(key)) {
        map.set(key, { ...item, passengerRef: item.passengerRef });
        result.push(map.get(key));
      } else {
        const existingItem = map.get(key);
        existingItem.passengerRef += ` ${item.passengerRef}`;
      }
    });

    return result;
  }

  openOsiModal(modal: any): void {
    this.buildOsiForm();
    this.modalService.open(modal, { size: "lg" });
  }

  private buildOsiForm() {
    this.osiForm = this.fb.group({
      osi: this.fb.array([this.buildEmptyOSI()]),
    });
  }

  private buildEmptyOSI(): FormGroup {
    return this.fb.group({
      osiCode: [""],
      actionCode: [""],
      text: [""],
      passengerRef: [""],
    });
  }

  addOSI() {
    const control = this.buildEmptyOSI();
    (this.osiForm.get("osi") as FormArray).push(control);
  }

  removeOSI(index: number) {
    const arrayControl = this.osiForm.get("osi") as FormArray;
    arrayControl.removeAt(index);
  }

  onPassengerRefChange(
    control: FormControl,
    isChecked: boolean,
    osiIdx: number,
    travIdx: number,
    passengerRef?: string,
  ) {
    let newPassengerRefValue: string;

    if (travIdx === -1) {
      if (isChecked) {
        newPassengerRefValue = "all";
        this.applyOsiByPassengerTypeDisabled[osiIdx] = true;
      } else {
        newPassengerRefValue = "";
        this.applyOsiByPassengerTypeDisabled[osiIdx] = false;
        this.selectedPassengersPerOsi = {};
      }
    } else {
      if (!this.selectedPassengersPerOsi[osiIdx]) {
        this.selectedPassengersPerOsi[osiIdx] = {};
      }
      this.selectedPassengersPerOsi[osiIdx][travIdx] = isChecked;

      const currentPassengerRefValue = control.get("passengerRef").value;

      if (currentPassengerRefValue.includes(passengerRef)) {
        newPassengerRefValue = currentPassengerRefValue
          .split(" ")
          .filter((ref) => ref !== passengerRef)
          .join(" ");
      } else {
        newPassengerRefValue = currentPassengerRefValue
          ? `${currentPassengerRefValue} ${passengerRef}`
          : passengerRef;
      }
    }

    control.get("passengerRef").setValue(newPassengerRefValue);
  }

  save() {
    const osi = this.helpers.getOSI(this.osiForm.get("osi").value);

    const body = {
      id: this.orderID,
      action: "add_other_service_information",
      metadata: {
        otherServiceInformation: osi,
      },
      passengerUpdate: {
        data: {
          name: this.passengers[0]?.data.name,
          birthdate: null,
        },
        travelerReference: this.passengers[0]?.travelerReference,
      },
    };
    this.orderUpdateProcess.isProcess = true;

    this.ndcApiService
      .orderUpdate(body)
      .then((response) => {
        this.orderUpdateProcess.isProcess = false;
        this.modalService.dismissAll();
        this._notificationSvc.success(
          this.translate.instant("NOTIFICATIONS.SUCCESS"),
          this.translate.instant(
            "ORDER_DETAIL.NOTIFICATIONS.PASSENGER_UPDATED",
          ),
          8000,
        );
        this.emitOrderUpdateSuccess.emit(true);
      })
      .catch((error) => {
        this.orderUpdateProcess.isProcess = false;
      });
  }
}
